import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { doc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useDoc } from '../../lib/hooks.js';
import { useAuth } from '../../lib/auth.js';
import { formatCents, formatTimestamp } from '../../lib/format.js';
import type { BillStatus } from '@sfp/shared/schemas';

interface BillData {
  docId?: string;
  source?: { type?: string };
  biller?: { name?: string; category?: string };
  accountNumberLast4?: string;
  invoiceNumber?: string;
  amountDue?: number;
  balanceTotal?: number;
  minPaymentDue?: number;
  dueDate?: { seconds: number };
  statementDate?: { seconds: number };
  paymentUrl?: string;
  payingEntityId?: string;
  status?: BillStatus;
  paidVia?: string;
  paidAt?: { seconds: number };
  paidProofDocId?: string;
  notes?: string;
}

const PAID_VIA = ['bank_billpay', 'biller_portal', 'auto_pay', 'check', 'wire', 'card', 'other'] as const;

export function BillDetail() {
  const { billId } = useParams<{ billId: string }>();
  const { data, loading } = useDoc<BillData>('bills', billId);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [paidVia, setPaidVia] = useState<(typeof PAID_VIA)[number]>('bank_billpay');
  const [notes, setNotes] = useState('');

  if (loading || !billId) return <div className="text-sm text-stone-400">Loading…</div>;
  if (!data) return <div className="text-sm text-stone-400">Not found.</div>;

  async function transition(next: BillStatus, extra: Record<string, unknown> = {}) {
    if (!user || !billId) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'bills', billId), {
        status: next,
        ...extra,
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'audit'), {
        userId: user.uid,
        action:
          next === 'paid'
            ? 'bill.mark_paid'
            : next === 'approved'
            ? 'bill.approve'
            : next === 'review'
            ? 'bill.review'
            : 'bill.dispute',
        billId,
        timestamp: serverTimestamp(),
        detail: extra,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <header>
        <Link to="/bills" className="text-xs text-stone-500 hover:underline">
          ← All bills
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight">
          {data.biller?.name ?? 'Unknown biller'}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-600">
          <span className="chip">{data.biller?.category}</span>
          {data.accountNumberLast4 && <span>···{data.accountNumberLast4}</span>}
          {data.invoiceNumber && <span>#{data.invoiceNumber}</span>}
        </div>
      </header>

      <section className="card p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Field label="Amount due" value={formatCents(data.amountDue)} large />
          <Field label="Due date" value={formatTimestamp(data.dueDate)} large />
          {data.balanceTotal != null && (
            <Field label="Statement balance" value={formatCents(data.balanceTotal)} />
          )}
          {data.minPaymentDue != null && (
            <Field label="Minimum payment" value={formatCents(data.minPaymentDue)} />
          )}
          <Field label="Statement date" value={formatTimestamp(data.statementDate)} />
          <Field label="Paying entity" value={data.payingEntityId ?? '—'} />
        </div>
      </section>

      {data.paymentUrl && (
        <a
          href={data.paymentUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary inline-flex"
        >
          Open biller portal ↗
        </a>
      )}

      {data.docId && (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/documents/${data.docId}`)}
        >
          View source document
        </button>
      )}

      <section className="card p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-stone-500">
          Workflow (mode B — track + paid-mark)
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Current status:</span>
          <span className="chip">{data.status ?? '—'}</span>
        </div>

        {data.status === 'received' && (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void transition('review')}
              disabled={busy}
            >
              Start review
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void transition('ignored')}
              disabled={busy}
            >
              Ignore (not a bill)
            </button>
          </div>
        )}

        {data.status === 'review' && (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void transition('approved', { approvedBy: user?.uid })}
              disabled={busy}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => void transition('disputed')}
              disabled={busy}
            >
              Dispute
            </button>
          </div>
        )}

        {data.status === 'approved' && (
          <div className="mt-4 space-y-2">
            <label className="block text-sm">
              <span className="block pb-1 font-medium text-stone-700">Paid via</span>
              <select
                value={paidVia}
                onChange={(e) => setPaidVia(e.target.value as (typeof PAID_VIA)[number])}
                className="w-full max-w-xs rounded border border-stone-300 px-2 py-1.5"
              >
                {PAID_VIA.map((p) => (
                  <option key={p}>{p.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block pb-1 font-medium text-stone-700">Notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded border border-stone-300 px-2 py-1.5"
                rows={2}
              />
            </label>
            <button
              type="button"
              className="btn-primary"
              onClick={() =>
                void transition('paid', {
                  paidVia,
                  paidAt: serverTimestamp(),
                  notes: notes || undefined,
                })
              }
              disabled={busy}
            >
              Mark paid
            </button>
          </div>
        )}

        {data.status === 'paid' && (
          <div className="mt-4 text-sm text-stone-600">
            Paid {formatTimestamp(data.paidAt)} via {data.paidVia ?? '—'}.
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, large }: { label: string; value: string; large?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-stone-500">{label}</div>
      <div className={large ? 'text-2xl font-bold text-stone-900' : 'text-stone-900'}>
        {value}
      </div>
    </div>
  );
}
